'use client';

import { useState, useContext, createContext, Fragment } from 'react';
import { Separator } from '@/components/ui/separator';

import AspectRatioSelector from './AspectRatioSelector';
import GenerationTabs from './GenerationTabs';
import GenerationModeSelector from './GenerationModeSelector';
import VariantsSelector from './VariantsSelector';
import PromptTextarea from './PromptTextarea';
import UploadImageSection from './UploadImageSection';
import SelectModelDialog from './SelectModelDialog';
import GenerateButton from './GenerateButton';
import GenderSelector from './GenderSelector';
import UseCaseSelector from './UseCaseSelector';

export enum GenerationTab {
  USE_CASE = 'use-case',
  FREE_FORM = 'free-form',
}

export enum GenerationMode {
  BASIC = 'basic',
  FAST = 'fast',
  ULTRA = 'ultra',
}

export enum AspectRatio {
  SQUARE = '1:1',
  LANDSCAPE = '4:5',
  PORTRAIT = '16:9',
}

interface ImageGenerationContextType {
  // Model / Preset
  model?: {
    title: string;
    src: string;
    idx: number;
    triggerWord?: string;
    modelUrl?: string;
    modelId: string;
  };
  setModel: (model: {
    title: string;
    src: string;
    idx: number;
    triggerWord?: string;
    modelUrl?: string;
    modelId: string;
  }) => void;
  // Generation Tab
  generationTab: GenerationTab;
  setGenerationTab: (tab: GenerationTab) => void;
  // Use Case Dialog
  useCase?: {
    title: string;
    src: string;
    idx: number;
  };
  setUseCase: (useCase: { title: string; src: string; idx: number }) => void;
  // Prompt
  prompt: string;
  setPrompt: (prompt: string) => void;
  // Variants
  variants: number;
  setVariants: (variants: number) => void;
  // Generation Mode
  generationMode: GenerationMode;
  setGenerationMode: (generationMode: GenerationMode) => void;
  // Aspect Ratio
  aspectRatio: AspectRatio;
  setAspectRatio: (aspectRatio: AspectRatio) => void;
  // Gender
  gender: 'male' | 'female' | 'neutral' | '';
  setGender: (gender: 'male' | 'female' | 'neutral' | '') => void;
  // Use Case
  useCaseType: string;
  setUseCaseType: (useCaseType: string) => void;
}

const ImageGenerationContext = createContext<
  ImageGenerationContextType | undefined
>(undefined);

export default function GenerateImageSidebar() {
  const [model, setModel] = useState<{
    title: string;
    src: string;
    idx: number;
    modelId: string;
    triggerWord?: string;
    modelUrl?: string;
  }>();

  const [generationTab, setGenerationTab] = useState<GenerationTab>(
    GenerationTab.USE_CASE,
  );

  const [useCase, setUseCase] = useState<{
    title: string;
    src: string;
    idx: number;
  }>();

  const [prompt, setPrompt] = useState('');
  const [variants, setVariants] = useState(1);
  const [generationMode, setGenerationMode] = useState(GenerationMode.BASIC);
  const [aspectRatio, setAspectRatio] = useState(AspectRatio.SQUARE);
  const [gender, setGender] = useState<'male' | 'female' | 'neutral' | ''>('');
  const [useCaseType, setUseCaseType] = useState('');
  return (
    <ImageGenerationContext.Provider
      value={{
        // Model / Preset
        model,
        setModel,
        // Generation Tab
        generationTab,
        setGenerationTab,
        // Use Case Dialog
        useCase,
        setUseCase,
        // Prompt
        prompt,
        setPrompt,
        // Variants
        variants,
        setVariants,
        // Generation Mode
        generationMode,
        setGenerationMode,
        // Aspect Ratio
        aspectRatio,
        setAspectRatio,
        // Gender
        gender,
        setGender,
        // Use Case
        useCaseType,
        setUseCaseType,
      }}
    >
      <div className='space-y-6 bg-gray-950 h-full'>
        {/* Model / Preset Dialog / SelectModelDialog */}
        <SelectModelDialog />

        {/* Tabs for Use Case and Free Form  / GenerationTabs */}
        <GenerationTabs />

        {/* Use Case Dialog (only if selected use-case tab) / SelectUsecaseDialog */}
        {/* {generationTab === GenerationTab.USE_CASE && <SelectUsecaseDialog />} */}

        {/* Prompt input and Add Photo section */}
        {generationTab === GenerationTab.FREE_FORM && (
          <Fragment>
            <PromptTextarea />
            {/* <UploadImageSection /> */}
          </Fragment>
        )}

        <GenderSelector />

        {generationTab === GenerationTab.USE_CASE && <UseCaseSelector />}

        {/*{generationTab === GenerationTab.FREE_FORM && <VariantsSelector />} */}

        {/* {generationTab === GenerationTab.FREE_FORM && (
          <GenerationModeSelector />
        )} */}

        {/* {generationTab === GenerationTab.FREE_FORM && <AspectRatioSelector />} */}

        <Separator className='bg-white/10' />

        <GenerateButton />
      </div>
    </ImageGenerationContext.Provider>
  );
}

export const useImageGeneration = () => {
  const context = useContext(ImageGenerationContext);
  if (!context) {
    throw new Error(
      'useImageGeneration must be used within ImageGenerationProvider',
    );
  }
  return context;
};
